import { DynamicImageInfo } from "@/components/canvas/utils/canvasUtils";
import { getContentFullUrl } from "@/lib/contentUtils";
import { showApiError } from "@/lib/toast";
import { uploadWithoutContent } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMutation } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { useSelector } from "react-redux";

interface Props {
  dynamicImages: DynamicImageInfo[];
  onReplace: (oldImageSrc: string, newImageSrc: string) => void;
}

export const CreateFromTemplateDynamicImagesForm: React.FC<Props> = ({ dynamicImages, onReplace }) => {
  const activeFirm = useSelector(selectActiveFirm);
  const [replacedImages, setReplacedImages] = useState<Record<number, File>>({});
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      uploadWithoutContent({
        firmGuid: String(activeFirm?.firmGuid),
        files: [file],
        name: file.name,
      }),
    onError: showApiError,
  });

  const handleImageReplace = async (index: number, file: File | null) => {
    const newReplacedImages = { ...replacedImages };

    if (file) {
      newReplacedImages[index] = file;
    } else {
      delete newReplacedImages[index];
    }

    const currentSrc = "src" in dynamicImages[index].object ? String(dynamicImages[index].object.src) : "";
    const uploadFileResponse = await uploadMutation.mutateAsync(file as File);
    const newImageSrc = getContentFullUrl(uploadFileResponse.data.value.contentStorageName);
    setReplacedImages(newReplacedImages);
    onReplace(currentSrc, newImageSrc);
  };

  const handleImageClick = (index: number) => {
    fileInputRefs.current[index]?.click();
  };

  const handleFileChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleImageReplace(index, file);
    // Reset the input value to allow selecting the same file again
    event.target.value = "";
  };

  return (
    <TooltipProvider>
      <div className="border rounded-lg p-2 flex gap-2">
        {dynamicImages.map((dynamicImage, index) => {
          const imageObject = dynamicImage.object;
          const currentSrc = String("src" in imageObject ? imageObject.src : "");
          const hasReplacement = replacedImages[index];

          return (
            <div key={index} className="flex items-center">
              {/* Hidden file input */}
              <input
                type="file"
                ref={(el) => (fileInputRefs.current[index] = el)}
                onChange={(e) => handleFileChange(index, e)}
                accept="image/*"
                className="hidden"
              />

              {currentSrc && !hasReplacement && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img
                      src={currentSrc}
                      alt={`Dynamic image ${index + 1}`}
                      className="w-12 h-12 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleImageClick(index)}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to replace the image</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Replacement Image Preview */}
              {hasReplacement && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img
                      src={URL.createObjectURL(hasReplacement)}
                      alt={`Replacement image ${index + 1}`}
                      className="w-12 h-12 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleImageClick(index)}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to replace the image</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
